import { PriceImprovementRaw } from 'components/common-form/sidePanel/priceEnhancer/priceImprovement/PriceImprovement';
import { connect } from 'react-redux';
import {
  priceImprovementCancelRequested,
  priceImprovementCommentChanged,
  priceImprovementCreateRequested,
  priceImprovementDiscarded,
  priceImprovementErrorDismissed,
  priceImprovementExpanded,
  priceImprovementExpirationTimeChanged,
  priceImprovementRequestedTradingOfferChanged,
} from 'state/quotes/priceEnhancements/priceImprovementRequest/priceImprovementRequest.actions';
import type {
  PriceImprovementExpirationTime,
  PriceImprovementStatus,
} from 'state/quotes/priceEnhancements/priceImprovementRequest/priceImprovementRequest.model';
import {
  getPriceImprovementCancellationError,
  getPriceImprovementComment,
  getPriceImprovementCreationError,
  getPriceImprovementExpanded,
  getPriceImprovementExpirationTime,
  getPriceImprovementReceivedPriceAdjustment,
  getPriceImprovementReceivedTradingOffer,
  getPriceImprovementRequestedTradingOffer,
  getPriceImprovementStatus,
  getPriceImprovementTraderComment,
} from 'state/quotes/priceEnhancements/priceImprovementRequest/priceImprovementRequest.selectors';
import type { PriceEnhancement, PriceEnhancementField } from 'state/quotes/trades/trades.model';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { ApiError } from 'utils/http/apiErrorHelper';

export interface OwnProps {
  tabId: string;
  quoteId: string;
  onFieldValueChanged: (field: PriceEnhancementField, value: PriceEnhancement[PriceEnhancementField]) => void;
}

export interface StateProps {
  isExpanded: boolean;
  status: PriceImprovementStatus | undefined;
  requestedTradingOffer: number | undefined;
  expirationTime: Nullable<PriceImprovementExpirationTime> | undefined;
  comment?: string;
  traderComment?: string;
  validatedTradingOffer?: number;
  validatedPriceAdjustment?: number;
  creationError?: ApiError;
  cancellationError?: ApiError;
}

export interface DispatchProps {
  onExpanded: (expanded: boolean) => void;
  onCommentChanged: (comment?: string) => void;
  onRequestedTradingOfferChanged: (requestedTradingOffer?: number) => void;
  onExpirationTimeChanged: (expirationTime: Nullable<PriceImprovementExpirationTime>) => void;
  onSubmit: () => void;
  onCancel: () => void;
  onDiscard: () => void;
  onErrorDismissed: () => void;
}

export type PriceImprovementProps = OwnProps & StateProps & DispatchProps;

const mapStateToProps = (state: AppState, { quoteId }: OwnProps): StateProps => {
  const expanded = getPriceImprovementExpanded(state, quoteId);
  const comment = getPriceImprovementComment(state, quoteId);
  const requestedTradingOffer = getPriceImprovementRequestedTradingOffer(state, quoteId);
  const expirationTime = getPriceImprovementExpirationTime(state, quoteId);
  const status = getPriceImprovementStatus(state, quoteId);
  const creationError = getPriceImprovementCreationError(state, quoteId);
  const cancellationError = getPriceImprovementCancellationError(state, quoteId);
  const traderComment = getPriceImprovementTraderComment(state, quoteId);
  const validatedTradingOffer = getPriceImprovementReceivedTradingOffer(state, quoteId);
  const validatedPriceAdjustment = getPriceImprovementReceivedPriceAdjustment(state, quoteId);

  return {
    isExpanded: expanded,
    requestedTradingOffer,
    expirationTime,
    comment,
    traderComment,
    validatedPriceAdjustment,
    validatedTradingOffer,
    status,
    creationError,
    cancellationError,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch, { quoteId }: OwnProps): DispatchProps => ({
  onExpanded: expanded => {
    dispatch(priceImprovementExpanded(quoteId, expanded));
  },
  onCommentChanged: comment => {
    dispatch(priceImprovementCommentChanged(quoteId, comment));
  },
  onRequestedTradingOfferChanged: requestedTradingOffer => {
    dispatch(priceImprovementRequestedTradingOfferChanged(quoteId, requestedTradingOffer));
  },
  onExpirationTimeChanged: expirationTime => {
    dispatch(priceImprovementExpirationTimeChanged(quoteId, expirationTime));
  },
  onSubmit: () => {
    dispatch(priceImprovementCreateRequested(quoteId));
  },
  onCancel: () => {
    dispatch(priceImprovementCancelRequested(quoteId));
  },
  onDiscard: () => {
    dispatch(priceImprovementDiscarded(quoteId));
  },
  onErrorDismissed: () => {
    dispatch(priceImprovementErrorDismissed(quoteId));
  },
});

export const ConnectedPriceImprovementRaw = connect(mapStateToProps, mapDispatchToProps)(PriceImprovementRaw);
